import React from 'react'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import { useHistory, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { InputField } from '../../../components/Forms'
import styles from '../CreateTripActivityPage/CreateTripActivityPage.module.scss'
import DefaultButton from '../../../components/Shared/Buttons/DefaultButton'
import classNames from 'classnames'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import GooglePlacesAutocomplete from '../../../components/Forms/GooglePlacesAutocomplete'

export interface EditTripFormValues {
  trip_name: string
  location: any
  location_uuid?: string
  start_date: Date | null
  end_date: Date | null
}

const EditTripSchema = Yup.object().shape({
  trip_name: Yup.string().required('Trip name is required'),
  start_date: Yup.date().nullable().required('Start date is required'),
  end_date: Yup.date()
    .nullable()
    .required('End date is required')
    .min(Yup.ref('start_date'), 'End date cannot be earlier than start date'),
  location: Yup.lazy((value) => {
    if (typeof value === 'string') {
      return Yup.string().trim().required('Location is required')
    } else if (typeof value === 'object' && value !== null) {
      return Yup.object().required('Location is required')
    }
    return Yup.mixed().required('Location is required')
  }),
})

interface EditTripFormProps {
  onSubmit: (values: EditTripFormValues) => void
  initialValues: EditTripFormValues
}

const EditTripForm: React.FC<EditTripFormProps> = ({ onSubmit, initialValues }) => {
  const history = useHistory()
  const { uuid } = useParams<{ uuid: string }>()

  const handleOnSubmit = async (
    values: EditTripFormValues,
    { setSubmitting }: FormikHelpers<EditTripFormValues>,
  ) => {
    try {
      await onSubmit(values)
      history.push(`/trips/${uuid}`)
    } catch (error) {
      console.error('Error submitting the form:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const inputClasses = classNames(styles.input)

  return (
    <div className={styles.formWrapper}>
      <Formik
        initialValues={initialValues}
        validationSchema={EditTripSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ isValid, dirty, values, setFieldValue, errors }) => (
          <Form>
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Trip Name</p>
                <InputField type="text" name="trip_name" placeholder="Trip Name" />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Location</p>
                <Field name="location" className={inputClasses}>
                  {({ field, form }: any) => (
                    <GooglePlacesAutocomplete {...field} form={form} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>Start Date</p>
                  <DateTimePicker
                    name="start_date"
                    onChange={(date) => setFieldValue('start_date', date)}
                    value={values.start_date}
                  />
                  {errors.start_date && (
                    <div className={styles.errorMsg}>{String(errors.start_date)}</div>
                  )}
                </div>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>End Date</p>
                  <DateTimePicker
                    name="end_date"
                    onChange={(date) => setFieldValue('end_date', date)}
                    value={values.end_date}
                  />
                  {errors.end_date && (
                    <div className={styles.errorMsg}>{String(errors.end_date)}</div>
                  )}
                </div>
              </div>
            </LocalizationProvider>

            <div className={styles.btnsWrapper}>
              <DefaultButton
                buttonStyles={{ marginRight: '16px' }}
                onClick={() => history.goBack()}
                outlined={true}
                title={'Cancel'}
              />
              <DefaultButton disabled={!isValid || !dirty} type={'submit'} title={'Update Trip'} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default EditTripForm
